import type {
  EvaluationContext,
  JsonValue,
  Provider,
  ResolutionDetails,
} from '@openfeature/web-sdk';
import {
  ErrorCode,
  OpenFeatureEventEmitter,
  ProviderEvents,
  ProviderStatus,
  StandardResolutionReasons,
} from '@openfeature/web-sdk';
import type { LDClient, LDFlagChangeset } from 'launchdarkly-js-client-sdk';

import { translateContext } from './translateContext';
import { translateResult } from './translateResult';

const wrongType = <T>(value: T): ResolutionDetails<T> => ({
  value,
  reason: StandardResolutionReasons.ERROR,
  errorCode: ErrorCode.TYPE_MISMATCH,
});

const makeResolver = <T>(typeName: string) =>
  (client: LDClient) =>
    (flagKey: string, defaultValue: T): ResolutionDetails<T> => {
      const res = client.variationDetail(flagKey, defaultValue);
      if (typeof res.value === typeName) return translateResult<T>(res);
      return wrongType(defaultValue);
    };

const resolveBoolean = makeResolver<boolean>('boolean');
const resolveString = makeResolver<string>('string');
const resolveNumber = makeResolver<number>('number');

const resolveObject = (client: LDClient) => <T extends JsonValue>(
  flagKey: string,
  defaultValue: T,
): ResolutionDetails<T> => {
  const res = client.variationDetail(flagKey, defaultValue);
  if (res.value !== null && typeof res.value === 'object') return translateResult<T>(res);
  return wrongType(defaultValue);
};

export const createLaunchDarklyClientProvider = (client: LDClient): Provider => {
  const events = new OpenFeatureEventEmitter();
  let status: ProviderStatus = ProviderStatus.NOT_READY;

  client.on('change', (changeset: LDFlagChangeset) => {
    events.emit(ProviderEvents.ConfigurationChanged, {
      flagsChanged: Object.keys(changeset),
    });
  });

  const initialize = async (): Promise<void> => {
    try {
      await client.waitForInitialization();
      status = ProviderStatus.READY;
      events.emit(ProviderEvents.Ready);
    } catch (err) {
      status = ProviderStatus.ERROR;
      events.emit(ProviderEvents.Error, { message: String(err) });
      throw err;
    }
  };

  const onContextChange = async (
    _old: EvaluationContext,
    next: EvaluationContext,
  ): Promise<void> => {
    await client.identify(translateContext(next));
  };

  return {
    metadata: { name: 'trm-launchdarkly-client-provider' },
    events,
    get status() {
      return status;
    },
    set status(s: ProviderStatus) {
      status = s;
    },
    initialize,
    onContextChange,
    resolveBooleanEvaluation: resolveBoolean(client),
    resolveStringEvaluation: resolveString(client),
    resolveNumberEvaluation: resolveNumber(client),
    resolveObjectEvaluation: resolveObject(client),
  };
};
